// src/views/ImageGallery.js
import React, { useState, useMemo } from 'react';
import { Carousel } from 'react-responsive-carousel';
import InteractiveImage from './InteractiveImage';

import { Box, IconButton, CircularProgress, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';


const ImageGallery = ({
  results,
  imageUrls,
  containerSize,
  selectedFaceId,
  onFaceClick,
  onStageClick,
  onSlideChange,
  onRemoveImage,
  isProcessed,
  isProcessing
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // --- 1. HELPER FUNCTION to check for any known identities ---
  const hasKnownIdentity = (result) => {
    // If there's no result, no faces, or the faces array is empty, it has no known identity.
    if (!result || !result.faces || result.faces.length === 0) {
      return false;
    }
    // Return true if we find at least one face that is NOT "Unknown Identity"
    return result.faces.some(face => face.assigned_identity !== "Unknown Identity");
  };

  const sortedResults = useMemo(() => {
    if (!isProcessed) {
      return results; // Keep original order before processing
    }

    return [...results].sort((a, b) => {
      const aHasKnown = hasKnownIdentity(a);
      const bHasKnown = hasKnownIdentity(b);

      // --- 2. UPDATED SORTING LOGIC ---
      // Rule 1: Prioritize images with at least one known identity.
      if (aHasKnown && !bHasKnown) {
        return -1; // a comes before b
      }
      if (!aHasKnown && bHasKnown) {
        return 1; // b comes before a
      }
      
      // Rule 2: If both have the same status (both known or both unknown), sort by year.
      const yearA = a.predicted_year ?? Infinity;
      const yearB = b.predicted_year ?? Infinity;
      
      return yearA - yearB;
    });
  }, [results, isProcessed]);


  const handleSlideChange = (index) => {
    setActiveIndex(index);
    if (onSlideChange) {
      const originalFilename = sortedResults[index].filename;
      const originalIndex = results.findIndex(res => res.filename === originalFilename);
      onSlideChange(originalIndex);
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', overflowY: 'auto'}}>
      
      {isProcessing && (
        <Box
          sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 20, display: 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            color: 'white', borderRadius: 1,
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Processing Images...
          </Typography>
        </Box>
      )}

      <Carousel
        showThumbs={true}
        showStatus={true}
        infiniteLoop={true}
        useKeyboardArrows={true}
        onChange={handleSlideChange}
        className="main-carousel"
        key={isProcessed ? 'processed' : 'unprocessed'}
      >
        {sortedResults.map((result) => (
          <Box
            key={result.filename}
            sx={{
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              height: '100%', position: 'relative',
            }}
          >
            {!isProcessed && (
              <IconButton
                onClick={() => onRemoveImage(result.filename)}
                sx={{
                  position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)',
                  zIndex: 10, backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                }}
                size="small"
              >
                <CloseIcon fontSize="small" sx={{ color: 'white' }} />
              </IconButton>
            )}

            {/* --- 3. UPDATED DISPLAY LOGIC for Predicted Year --- */}
            {isProcessed && result.predicted_year != null && hasKnownIdentity(result) && (
              <Typography
                variant="h4"
                sx={{
                  position: 'absolute', top: 8, left: "30%", zIndex: 10,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)', color: 'white',
                  px: 1, py: 0.5, borderRadius: 1,
                }}
              >
                Predicted Year: {result.predicted_year}
              </Typography>
            )}

            <InteractiveImage
              imageUrl={imageUrls[result.filename]}
              faces={result.faces}
              selectedFaceId={selectedFaceId}
              onFaceClick={onFaceClick}
              onStageClick={onStageClick}
              containerSize={containerSize}
            />
          </Box>
        ))}
      </Carousel>

      {/* --- 4. UPDATED DISPLAY LOGIC for Posterior --- */}
      {isProcessed && hasKnownIdentity(sortedResults[activeIndex]) && sortedResults[activeIndex]?.year_posterior_url && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <img
            src={sortedResults[activeIndex].year_posterior_url}
            alt="Overall dating posterior distribution"
            style={{
              maxWidth: '100%', maxHeight: '100%',
              borderRadius: '4px', border: '1px solid #ddd',
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ImageGallery;